import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Phone, Mail, MapPin, Clock, Facebook, Twitter, Instagram, Linkedin } from 'lucide-react';
import axios from 'axios';
import { API, toast } from '@/App';
import logoBR from '@/assets/logo-br.png';

const FOOTER_PRIMARY_LINKS = [
  { label: 'Accueil', path: '/' },
  { label: 'Nos services', path: '/services' },
  { label: 'Suivre un colis', path: '/track' },
  { label: 'Tarifs', path: '/pricing' },
  { label: 'Vendeurs', path: '/vendors' },
  { label: 'Intégrations', path: '/integrations' },
  { label: 'Blog', path: '/blog' },
];

const FOOTER_SUPPORT_LINKS = [
  { label: 'FAQ', path: '/faq' },
  { label: 'Contact', path: '/contact' },
  { label: 'Programme ambassadeur', path: '/ambassador-program' },
  { label: 'Mentions légales', path: '/legal/mentions' },
  { label: 'Politique de confidentialité', path: '/privacy' },
  { label: "Conditions d'utilisation", path: '/terms' },
];

const FooterNew = () => {
  const currentYear = new Date().getFullYear();
  const [newsletterEmail, setNewsletterEmail] = useState('');
  const [newsletterFirstName, setNewsletterFirstName] = useState('');
  const [newsletterLoading, setNewsletterLoading] = useState(false);

  const handleInternalLinkClick = (event) => {
    const link = event.target.closest?.('a[href^="/"]');
    if (link) {
      window.setTimeout(() => window.scrollTo({ top: 0, left: 0, behavior: 'auto' }), 0);
    }
  };

  const handleNewsletterSubscribe = async (event) => {
    event.preventDefault();
    if (!newsletterEmail.trim()) {
      toast.error('Email requis pour la newsletter');
      return;
    }

    setNewsletterLoading(true);
    try {
      await axios.post(`${API}/newsletter/subscribe`, {
        first_name: newsletterFirstName || null,
        email: newsletterEmail.trim(),
        source: 'footer',
        segment: 'visitors',
        subscribed: true,
      });
      toast.success('Inscription newsletter confirmée');
      setNewsletterEmail('');
      setNewsletterFirstName('');
    } catch (error) {
      toast.error(error.response?.data?.detail || "Impossible de vous inscrire à la newsletter");
    } finally {
      setNewsletterLoading(false);
    }
  };

  return (
    <footer className="bg-gray-900 text-gray-300 mt-auto" onClick={handleInternalLinkClick}>
      <div className="container mx-auto px-4 py-12">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
          <div>
            <div className="flex items-center gap-2 mb-4">
              <div className="h-16 w-48">
                <img src={logoBR} alt="Coli Noki Logo" className="h-16 w-32 object-cover object-left " />
              </div>
            </div>
            <p className="text-sm text-gray-400 mb-4">
              Plateforme moderne de livraison, suivi colis et gestion logistique digitale au Congo.
            </p>
            <div className="flex gap-3">
              <a
                href="https://facebook.com"
                target="_blank"
                rel="noopener noreferrer"
                className="w-9 h-9 bg-gray-800 hover:bg-red-600 rounded-full flex items-center justify-center transition-colors"
                aria-label="Facebook"
              >
                <Facebook className="w-4 h-4" />
              </a>
              <a
                href="https://twitter.com"
                target="_blank"
                rel="noopener noreferrer"
                className="w-9 h-9 bg-gray-800 hover:bg-cyan-500 rounded-full flex items-center justify-center transition-colors"
                aria-label="Twitter"
              >
                <Twitter className="w-4 h-4" />
              </a>
              <a
                href="https://instagram.com"
                target="_blank"
                rel="noopener noreferrer"
                className="w-9 h-9 bg-gray-800 hover:bg-pink-600 rounded-full flex items-center justify-center transition-colors"
                aria-label="Instagram"
              >
                <Instagram className="w-4 h-4" />
              </a>
              <a
                href="https://linkedin.com"
                target="_blank"
                rel="noopener noreferrer"
                className="w-9 h-9 bg-gray-800 hover:bg-blue-600 rounded-full flex items-center justify-center transition-colors"
                aria-label="LinkedIn"
              >
                <Linkedin className="w-4 h-4" />
              </a>
            </div>

            <div className="mt-6">
              <p className="text-sm font-semibold text-white mb-2">Newsletter</p>
              <form onSubmit={handleNewsletterSubscribe} className="space-y-2">
                <input
                  type="text"
                  value={newsletterFirstName}
                  onChange={(e) => setNewsletterFirstName(e.target.value)}
                  placeholder="Prénom (optionnel)"
                  className="w-full rounded bg-gray-800 border border-gray-700 px-3 py-2 text-sm text-gray-100 placeholder:text-gray-500 focus:outline-none focus:ring-1 focus:ring-cyan-500"
                />
                <input
                  type="email"
                  value={newsletterEmail}
                  onChange={(e) => setNewsletterEmail(e.target.value)}
                  placeholder="votre@email.com"
                  className="w-full rounded bg-gray-800 border border-gray-700 px-3 py-2 text-sm text-gray-100 placeholder:text-gray-500 focus:outline-none focus:ring-1 focus:ring-cyan-500"
                  required
                />
                <button
                  type="submit"
                  disabled={newsletterLoading}
                  className="w-full rounded bg-red-600 hover:bg-red-700 disabled:opacity-60 disabled:cursor-not-allowed px-3 py-2 text-sm font-medium text-white transition-colors"
                >
                  {newsletterLoading ? 'Inscription...' : "S'inscrire"}
                </button>
              </form>
            </div>
          </div>

          <div>
            <h3 className="text-white font-semibold text-lg mb-4">Liens rapides</h3>
            <ul className="space-y-2">
              {FOOTER_PRIMARY_LINKS.map((item) => (
                <li key={item.path}>
                  <Link to={item.path} className="text-sm hover:text-red-500 transition-colors">
                    {item.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <h3 className="text-white font-semibold text-lg mb-4">Ressources</h3>
            <ul className="space-y-2">
              {FOOTER_SUPPORT_LINKS.map((item) => (
                <li key={item.path}>
                  <Link to={item.path} className="text-sm hover:text-red-500 transition-colors">
                    {item.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <h3 className="text-white font-semibold text-lg mb-4">Contact</h3>
            <ul className="space-y-3">
              <li className="flex items-start gap-2">
                <Phone className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-sm">+242 05 087 12 50</p>
                  <p className="text-sm">+242 06 476 45 82</p>
                </div>
              </li>
              <li className="flex items-start gap-2">
                <Mail className="w-5 h-5 text-cyan-500 flex-shrink-0 mt-0.5" />
                <a href="mailto:contact@colinoki.com" className="text-sm hover:text-cyan-500 transition-colors">
                  contact@colinoki.com
                </a>
              </li>
              <li className="flex items-start gap-2">
                <MapPin className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
                <p className="text-sm">Brazzaville, Pointe-Noire, Kinshasa</p>
              </li>
              <li className="flex items-start gap-2">
                <Clock className="w-5 h-5 text-amber-500 flex-shrink-0 mt-0.5" />
                <div className="text-sm">
                  <p>Lun - Ven : 8h00 - 18h00</p>
                  <p>Sam : 9h00 - 13h00</p>
                </div>
              </li>
            </ul>
          </div>
        </div>
      </div>

      <div className="border-t border-gray-800">
        <div className="container mx-auto px-4 py-6">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            <p className="text-sm text-gray-400 text-center md:text-left">
              (c) {currentYear} Coli Noki. Tous droits réservés.
            </p>
            <p className="text-sm text-gray-400 text-center md:text-right">
              Plateforme professionnelle de gestion de livraisons
            </p>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default FooterNew;


